import * as React from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
  GridToolbar,
  isColumnMenuSortActive,
  isColumnMenuFilterActive,
} from '@progress/kendo-react-grid';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { DataResult, process, State } from '@progress/kendo-data-query';
import { Dispatch } from 'redux';
import { useEffect } from 'react';
import {
  LocalizationProvider,
  loadMessages,
  load,
  IntlProvider,
} from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from '../AlertsGrid/GridLocale';
import { getDateTimeLocaleString } from '../../utils/utils';
import { LoadingPanel } from '../LoadingPanel';
import { FetchError } from '../../Interfaces/FetchError';
import { getCommandInfo } from '../Commands/GetCommandInfo';

import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import {
  fetchAssetStatusesByQuery,
  resetAssetStatusesFetchError,
  clearCommandNotificationSuccessMessage,
  sendDeviceCommand,
} from '../../store/Devices/AssetStatesActions';
import { AssetStateRecord } from '../../Interfaces/Assets/AssetState';
import { initialAssetStatusesGridDataState } from '../../store/UserSettings/UserSettingsState';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import AssetStatusesFilter from './AssetStatusesFilter';
import { Splitter, SplitterOnChangeEvent } from '@progress/kendo-react-layout';
import {
  HeaderThElement,
  setGroupIds,
  DetailExpandDescriptor,
} from '@progress/kendo-react-data-tools';
import { saveAssetStatusesGridState } from '../../store/UserSettings/UserSettingsActions';
import StringResourceManager, { GetString } from '../StringResourceManager';
import esMessages from '../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import weekData from 'cldr-core/supplemental/weekData.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import caGregorian from 'cldr-dates-full/main/fr/ca-gregorian.json';
import numbers from 'cldr-numbers-full/main/fr/numbers.json';
import dateFields from 'cldr-dates-full/main/fr/dateFields.json';
import timeZones from 'cldr-dates-full/main/fr/timeZoneNames.json';
load(
  likelySubtags,
  currencyData,
  weekData,
  caGregorian,
  numbers,
  dateFields,
  timeZones
);
import { CommandInfo } from '../../Interfaces/Commands/CommandInfo';
import { ApiEnums } from '../../Interfaces/ApiEnums';
import { NodeInfo } from '../../Interfaces/Assets/AssetInfo';
import { CSVLink } from 'react-csv';
import { dateFormat } from '../../utils/utils';
import { AssetStateDetail } from './AssetStateDetail';
import { StatusFilterCell } from './StatusFilterCell';
import { Dialog } from '@progress/kendo-react-dialogs';
import NodeAssetCategoryStatusesGrid from './NodeAssetCategoryStatusesGrid';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
import {
  SplitButton,
  SplitButtonItemClickEvent,
  SplitButtonClickEvent,
} from '@progress/kendo-react-buttons';
import {
  ColumnMenu,
  CustomCell,
  HeaderCustomCell,
} from '../ColumnMenus/ColumnMenu';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    error: {
      fontSize: '1.5rem',
    },
    toolbar: {
      height: 65,
      alignItems: 'flex-end',
      padding: theme.spacing(1),
      gap: 10,
    },
    toolbarButton: {
      height: 40,
    },
  })
);

const DateTimeCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  if (dataItem?.TimeStamp && dataItem.TimeStamp != '0001-01-01T00:00:00') {
    return (
      <td style={{ fontSize: '0.9rem' }}>
        {getDateTimeLocaleString(dataItem.TimeStamp)}
      </td>
    );
  }
  return <td></td>;
};

const getStatusIconName = (status: ApiEnums.DeviceStatus) => {
  switch (status) {
    case ApiEnums.DeviceStatus.Ok:
      return 'StatusOk.png';
    case ApiEnums.DeviceStatus.Inconvenienced:
      return 'StatusInconvenience.png';
    case ApiEnums.DeviceStatus.Error:
      return 'StatusError.png';
    default:
      return 'StatusUnknown.png';
  }
};

const cellRender = (
  td: React.ReactElement<HTMLTableCellElement> | null,
  props: GridCellProps
) => {
  if (
    td &&
    td.props.children &&
    props.rowType === 'groupHeader' &&
    props.dataItem.items.length > 0 &&
    props.dataItem.items[0].Status !== undefined
  ) {
    let groupStatus = getGroupStatus(props.dataItem.items);
    let children = (
      <span>
        {td.props.children}
        <StringResourceManager stringKey="STATUS_HEADER" variant="inherit" />
        <img src={'/assets/images/' + getStatusIconName(groupStatus)} alt="" />
      </span>
    );
    return React.cloneElement(td, td.props, children);
  }
  return td;
};

const StatusCellIcon = (props: GridCellProps) => {
  if (props.dataItem?.Status !== undefined) {
    return (
      <td>
        <img
          src={'/assets/images/' + getStatusIconName(props.dataItem?.Status)}
          alt=""
        />
      </td>
    );
  }

  return null;
};

const getGroupStatus = (items: AssetStateRecord[]): ApiEnums.DeviceStatus => {
  let groupStatus: ApiEnums.DeviceStatus = ApiEnums.DeviceStatus.Unknown;
  for (let i = 0; i < items.length; ++i) {
    if (items[i].Status > groupStatus) {
      groupStatus = items[i].Status;
    }
    if (groupStatus == ApiEnums.DeviceStatus.Error) {
      break;
    }
  }
  return groupStatus;
};

//
/* Useful function to recursively determine the overall group status (when having N levels of groupings)
const getGroupStatusRecursive = (arr: any[], currentStatus: number): number => {
  return arr.reduce((accumulator, item) => {
    if (item.Status) {
      if (item.Status > accumulator) accumulator = item.Status;
      return accumulator;
    }

    if (item.items) return getGroupStatusRecursive(item.items, accumulator);

    return accumulator;
  }, currentStatus);
};
*/

const getStatusColor = (status: ApiEnums.DeviceStatus) => {
  switch (status) {
    case ApiEnums.DeviceStatus.Ok:
      return 'rgba(34, 129, 37, 1)';
    case ApiEnums.DeviceStatus.Inconvenienced:
      return 'rgba(229, 104, 0, 1)';
    case ApiEnums.DeviceStatus.Error:
      return 'rgba(213, 46, 34, 1)';
    default:
      return 'rgba(112, 112, 112, 1)';
  }
};

const CurrentIssueCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;

  if (dataItem?.CurrentIssue) {
    let color: string = getStatusColor(dataItem?.Status);
    return (
      <td style={{ color: color, fontWeight: 'bold', fontSize: '0.9rem' }}>
        {dataItem.CurrentIssue}
      </td>
    );
  }

  return <td></td>;
};

interface DeviceCommandsCell extends GridCellProps {
  sendCommandInfo: (record: AssetStateRecord, commandInfo: CommandInfo) => void;
}

const DeviceCommandsCell = (props: DeviceCommandsCell) => {
  const items: object[] = [
    { text: GetString('COMMAND_RESET_DEVICE') },
    { text: GetString('COMMAND_REBOOT_LANE') },
  ];

  const dataItem = props.dataItem;
  if (!dataItem?.NodeInfo?.NodeID) return <td></td>;

  // TODO: Show modal for the user to enter notes/details?
  const handleButtonClick = (event: SplitButtonClickEvent) => {
    let commandInfo = getCommandInfo(dataItem.Category);

    props.sendCommandInfo(props.dataItem, commandInfo);
  };

  const handleMenuItemClick = (event: SplitButtonItemClickEvent) => {
    if (event.itemIndex === 0) {
      let commandInfo = getCommandInfo(dataItem.Category);
      props.sendCommandInfo(props.dataItem, commandInfo);
    } else if (event.itemIndex === 1) {
      let commandInfo: CommandInfo = { Command: 'Reboot', Parameters: '' };
      props.sendCommandInfo(props.dataItem, commandInfo);
    }
  };

  return (
    <td>
      <SplitButton
        text={GetString('COMMAND_RESET_DEVICE')}
        items={items}
        onButtonClick={handleButtonClick}
        onItemClick={handleMenuItemClick}
        disabled={dataItem.Status <= ApiEnums.DeviceStatus.Ok}
      />
    </td>
  );
};

interface Props {
  setLastRefreshQuery: (lastRefreshDate: Date) => void;
}

interface AssetStateCSVRecord {
  Store: NodeInfo['SiteNumber'];
  Cluster: NodeInfo['NodeGroup'];
  Lane: NodeInfo['LaneNumber'];
  Division: NodeInfo['Division'];
  Category: string;
  Manufacturer: string;
  Model: string;
  SerialNumber: string;
  FirmwareVersion: string;
  Status: string;
  TimeStamp: string;
  CurrentIssue: string;
}

const AssetStatusesGrid: React.FC<Props> = ({
  setLastRefreshQuery,
}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const status = GetString('STATUS');
  const statuses = GetString('STATUSES');

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const loading: boolean = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.isFetchingAssetStatuses
  );

  const assetStatuses: AssetStateRecord[] = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.assetStatuses
  );

  useEffect(() => {
    setDataResult(processWithGroups(assetStatuses, dataState));
  }, [assetStatuses]);

  const dataState: State = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.assetStatusGridDataState
  );

  const fetchError: FetchError = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.fetchError
  );

  const notificationSuccessMessage: string = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.notificationSuccessMessage
  );

  const commandNotificationRebootSuccessMessage: string = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.notificationRebootSuccessMessage
  );

  const [selectedNodeID, setSelectedNodeID] = React.useState<number>(0);
  const [selectedCategory, setSelectedCategory] = React.useState<string>('');

  const [CategoryStatusesModalVisible, setCategoryStatusesModalVisible] =
    React.useState<boolean>(false);

  const [categoryStatusesTitle, setCategoryStatusesTitle] =
    React.useState<string>();

  const [uniqueKeyOnRefresh, setUniqueKeyOnRefresh] = React.useState<Date>(
    new Date()
  );

  useEffect(() => {
    let timeoutId: any;
    if (notificationSuccessMessage) {
      timeoutId = setTimeout(() => {
        resetNotificationSuccessMessage();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [notificationSuccessMessage]);

  useEffect(() => {
    let timeoutId: any;
    if (commandNotificationRebootSuccessMessage) {
      timeoutId = setTimeout(() => {
        resetNotificationSuccessMessage();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [commandNotificationRebootSuccessMessage]);

  const resetNotificationSuccessMessage = () => {
    clearCommandNotificationSuccessMessage(dispatch, commandInfo);
  };

  const processWithGroups = (data: any[], dataState: State): DataResult => {
    if (dataState === undefined) {
      dataState = initialAssetStatusesGridDataState;
    }

    const newDataState: DataResult = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const [dataResult, setDataResult] = React.useState<DataResult>(
    processWithGroups(assetStatuses, dataState)
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(assetStatuses, event.dataState);

    saveAssetStatusesGridState(dispatch, event.dataState);
    setDataResult(newDataState);
  };

  useEffect(() => {
    let timeoutId: any;
    if (fetchError.StatusCode > 0 && fetchError.Message != '') {
      displayError(true);
      timeoutId = setTimeout(() => {
        clearFetchError();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [fetchError]);

  const [error, displayError] = React.useState(false);

  const clearFetchError = () => {
    displayError(false);
    resetAssetStatusesFetchError(dispatch);
  };

  const onFetchAssetStatuses = () => {
    let newDataState: State = {
      skip: 0,
      take: dataState.take,
      sort: dataState.sort,
      group: dataState.group,
    };
    saveAssetStatusesGridState(dispatch, newDataState);
    setLastRefreshQuery(new Date());
    setUniqueKeyOnRefresh(new Date());
    fetchAssetStatusesByQuery(dispatch);
  };

  const [verticalPanes, setVerticalPanes] = React.useState<Array<any>>([
    {},
    {},
  ]);

  const onVerticalChange = (event: SplitterOnChangeEvent) => {
    setVerticalPanes(event.newState);
  };

  var newData = dataResult.data;
  const [commandInfo, setCommandInfo] = React.useState<
    CommandInfo | undefined
  >();

  const sendCommandInfo = (
    record: AssetStateRecord,
    commandInfo: CommandInfo
  ) => {
    sendDeviceCommand(dispatch, record, commandInfo);
    setCommandInfo(commandInfo);
  };

  const CommandsCellWrapper = (props: GridCellProps) => (
    <DeviceCommandsCell {...props} sendCommandInfo={sendCommandInfo} />
  );

  useEffect(() => {
    setLastRefreshQuery(new Date());
    fetchAssetStatusesByQuery(dispatch);
  }, []);

  const [csvData, setCSVData] = React.useState<AssetStateCSVRecord[]>([]);

  const getCSVFileName = () => {
    return `Assets_Status_export_${dateFormat(currentLocale.language)}`;
  };

  const processExportCSV = () => {
    let result: AssetStateCSVRecord[] = [];

    if (Array.isArray(assetStatuses)) {
      assetStatuses.forEach(nodeAsset => {
        let data: AssetStateCSVRecord = {
          Store: nodeAsset.NodeInfo.SiteNumber,
          Cluster: nodeAsset.NodeInfo.NodeGroup,
          Lane: nodeAsset.NodeInfo.LaneNumber,
          Division: nodeAsset.NodeInfo.Division,
          Category: nodeAsset.Category,
          Manufacturer: nodeAsset.Manufacturer,
          Model: nodeAsset.Model,
          SerialNumber: nodeAsset.SerialNumber,
          FirmwareVersion: nodeAsset.FirmwareVersion,
          Status: convertStatusToString(nodeAsset.Status),
          CurrentIssue: nodeAsset.CurrentIssue,
          TimeStamp: getDateTimeLocaleString(nodeAsset.TimeStamp),
        };
        result.push(data);
      });
    }
    setCSVData(result);
  };

  const convertStatusToString = (status: ApiEnums.DeviceStatus) => {
    switch (status) {
      case ApiEnums.DeviceStatus.Ok:
        return 'Ok';
      case ApiEnums.DeviceStatus.Inconvenienced:
        return 'Inconvenienced';
      case ApiEnums.DeviceStatus.Error:
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  useEffect(() => {
    exportCSV();
  }, [csvData]);

  const exportCSV = () => {
    if (csvData.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();
    }
  };

  const [detailExpand, setDetailExpand] =
    React.useState<DetailExpandDescriptor>({ [newData[1]]: true });

  const CategoryCell = (props: GridCellProps) => {
    if (props.dataItem?.Category !== '')
      return (
        <td>
          <Button
            style={{
              fontWeight: 'bold',
              padding: '0',
              textDecorationLine: 'underline',
              fontSize: '0.9rem',
            }}
            themeColor={'primary'}
            fillMode="link"
            type="button"
            onClick={() =>
              onCategoryClicked(
                props.dataItem?.NodeInfo?.NodeID,
                props.dataItem?.Category,
                `${statuses}: ${props.dataItem?.Category}`
              )
            }
          >
            {props.dataItem?.Category}
          </Button>
        </td>
      );

    return <td></td>;
  };

  const onCategoryClicked = (nodeID: any, category: string, title: string) => {
    setSelectedNodeID(nodeID);
    setSelectedCategory(category);
    setCategoryStatusesTitle(title);
    toggleDialog();
  };

  const toggleDialog = () => {
    setCategoryStatusesModalVisible(!CategoryStatusesModalVisible);
  };

  const dateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="TimeStamp"
      />
    );
  };

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: State) => {
    return (
      isColumnMenuFilterActive(field, dataState.filter) ||
      isColumnMenuSortActive(field, dataState.sort)
    );
  };

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <CSVLink
            data={csvData}
            ref={csvLinkRef}
            filename={getCSVFileName() + '.csv'}
          />
          {CategoryStatusesModalVisible && (
            <Dialog title={categoryStatusesTitle} onClose={toggleDialog}>
              <NodeAssetCategoryStatusesGrid
                nodeID={selectedNodeID}
                category={selectedCategory}
              />
            </Dialog>
          )}
          {loading && LoadingPanel}
          <Splitter
            panes={verticalPanes}
            orientation={'vertical'}
            onChange={onVerticalChange}
          >
            <AssetStatusesFilter />
            <Grid
              key={`${uniqueKeyOnRefresh}`}
              style={{ height: '73vh' }}
              data={dataResult}
              {...dataState}
              onDataStateChange={dataStateChange}
              detail={AssetStateDetail}
              resizable={true}
              sortable={true}
              filterable={true}
              pageable={{
                buttonCount: 4,
                pageSizes: [100, 500, 1000, 5000],
              }}
              groupable={true}
              reorderable={true}
              fixedScroll={false}
              dataItemKey="RowID"
              defaultDetailExpand={detailExpand}
              cells={{
                headerCell: HeaderCustomCell,
                data: CustomCell,
              }}
            >
              <GridToolbar>
                <Button
                  onClick={onFetchAssetStatuses}
                  type="submit"
                  className={classes.toolbarButton}
                >
                  <StringResourceManager stringKey="RUN_QUERY" />
                </Button>
                <Button
                  className={classes.toolbarButton}
                  onClick={processExportCSV}
                  type="submit"
                >
                  <StringResourceManager stringKey="DOWNLOAD_ASSETS" />
                </Button>
              </GridToolbar>
              <GridColumn
                {...columnProps('NodeInfo.Division')}
                title={GetString('COLUMN_TITLE_DIVISION')}
                width="110px"
                columnMenu={ColumnMenu}
                filterable={false}
              />
              <GridColumn
                {...columnProps('NodeInfo.SiteNumber')}
                title={GetString('COLUMN_TITLE_STORE')}
                width="110px"
                columnMenu={ColumnMenu}
                filterable={false}
              />
              <GridColumn
                {...columnProps('NodeInfo.NodeGroup')}
                title={GetString('COLUMN_TITLE_CLUSTER')}
                width="110px"
                columnMenu={ColumnMenu}
                filterable={false}
              />
              <GridColumn
                {...columnProps('NodeInfo.LaneNumber')}
                title={GetString('COLUMN_TITLE_LANE')}
                width="110px"
                columnMenu={ColumnMenu}
                filterable={false}
              />
              <GridColumn
                {...columnProps('Category')}
                title={GetString('COLUMN_TITLE_LANE_CATEGORY')}
                width="150px"
                cells={{ data: CategoryCell }}
                columnMenu={ColumnMenu}
                filterable={false}
              />
              <GridColumn
                {...columnProps('Model')}
                title={GetString('MODEL')}
                width="145px"
                columnMenu={ColumnMenu}
                filterable={false}
              />
              <GridColumn
                field="Status"
                title={GetString('STATUS')}
                width="175px"
                cells={{ data: StatusCellIcon, filterCell: StatusFilterCell }}
              />
              <GridColumn
                {...columnProps('CurrentIssue')}
                title={GetString('CURRENT_ISSUE')}
                width="150px"
                cells={{ data: CurrentIssueCell }}
                columnMenu={ColumnMenu}
                filterable={false}
              />
              <GridColumn
                field="Commands"
                title={GetString('COLUMN_TITLE_COMMANDS')}
                width="165px"
                filterable={false}
                cells={{ data: CommandsCellWrapper }}
              />
              <GridColumn
                field="TimeStamp"
                title={GetString('COLUMN_TITLE_LANE_UPDATED')}
                width="250px"
                cells={{ data: DateTimeCell, filterCell: dateFilterCell }}
              />
            </Grid>
            <Fade>
              <NotificationGroup className={classes.bottomRight}>
                {error && (
                  <Notification
                    className={classes.error}
                    type={{
                      style: 'error',
                      icon: true,
                    }}
                    closable={true}
                    onClose={() => clearFetchError()}
                  >
                    <StringResourceManager stringKey={fetchError.Message} />
                    {`(${status}: ${fetchError.StatusCode})`}
                  </Notification>
                )}
                {notificationSuccessMessage && (
                  <Notification
                    className={classes.error}
                    type={{
                      style: 'success',
                      icon: true,
                    }}
                    closable={true}
                    onClose={resetNotificationSuccessMessage}
                  >
                    <StringResourceManager
                      stringKey={notificationSuccessMessage}
                    />
                  </Notification>
                )}
                {commandNotificationRebootSuccessMessage && (
                  <Notification
                    className={classes.error}
                    type={{
                      style: 'success',
                      icon: true,
                    }}
                    closable={true}
                    onClose={resetNotificationSuccessMessage}
                  >
                    <StringResourceManager
                      stringKey={commandNotificationRebootSuccessMessage}
                    />
                  </Notification>
                )}
              </NotificationGroup>
            </Fade>
          </Splitter>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};

export default AssetStatusesGrid;
